import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const FEATURES = [
  {
    icon: <FavoriteIcon />,
    title: 'Merkezi Bağış Takibi',
    desc: 'Tüm bağışları tek panelden yönetin, bağışçı bazlı detaylı kayıtlar tutun.',
  },
  {
    icon: <TrendingUpIcon />,
    title: 'Kampanya Bazlı İzleme',
    desc: 'Hangi kampanya ne kadar bağış aldı? Anlık olarak takip edin.',
  },
  {
    icon: <AccountBalanceIcon />,
    title: 'POS & Banka Entegrasyonu',
    desc: 'Sanal POS ve banka hesaplarıyla otomatik bağış tahsilatı.',
  },
  {
    icon: <AssessmentIcon />,
    title: 'Detaylı Raporlama',
    desc: 'Tarih, kampanya ve bağışçı bazlı raporlar oluşturun.',
  },
  {
    icon: <SecurityIcon />,
    title: 'Şeffaf ve Güvenli Kayıtlar',
    desc: 'Tüm işlemler kayıt altında, denetlenebilir ve güvenli.',
  },
];

const BagisYonetimiPage = () => {
  return (
    <Box>

      {/* ===== HERO ===== */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 65%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2.4rem', md: '3.2rem' },
              mb: 2,
            }}
          >
            Bağış Yönetimi
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.05rem', md: '1.2rem' },
              color: '#64748b',
              lineHeight: 1.7,
              mb: 4,
            }}
          >
            Bağış süreçlerini karmaşadan çıkarın.  
            Bağışçıları, kampanyaları ve tahsilatları tek merkezden yönetin.
          </Typography>

          <Button
            variant="contained"
            size="large"
            sx={{ borderRadius: 3, px: 5, py: 1.5 }}
          >
            Demo Talep Edin
          </Button>
        </Container>
      </Box>

      {/* ===== VALUE SECTION ===== */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography
                sx={{ fontWeight: 800, fontSize: '1.6rem', mb: 2 }}
              >
                Ne Sağlar?
              </Typography>

              <Typography sx={{ color: '#64748b', lineHeight: 1.7 }}>
                Bağış Yönetimi modülü, derneğinizin tüm bağış süreçlerini
                şeffaf, düzenli ve raporlanabilir hale getirir.
                Manuel takip, dağınık tablolar ve belirsizlik ortadan kalkar.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  'Bağışçı bazlı kayıt ve geçmiş',
                  'Kampanya ve hedef takibi',
                  'Online tahsilat ve banka entegrasyonu',
                  'Şeffaf raporlama ve denetim',
                ].map((item, i) => (
                  <Typography key={i} sx={{ fontWeight: 600 }}>
                    • {item}
                  </Typography>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== HOW IT WORKS ===== */}
      <Box sx={{ py: 10, background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Typography
            align="center"
            sx={{ fontWeight: 800, fontSize: '1.6rem', mb: 6 }}
          >
            Nasıl Çalışır?
          </Typography>

          <Grid container spacing={4}>
            {[
              'Bağış yöntemlerini tanımlayın',
              'Kampanyalarınızı oluşturun',
              'Online bağışları toplayın',
              'Raporları anlık görüntüleyin',
            ].map((step, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>
                    {i + 1}. Adım
                  </Typography>
                  <Typography sx={{ color: '#64748b' }}>
                    {step}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== FEATURES GRID ===== */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                    transition: 'all .3s',
                    '&:hover': {
                      boxShadow: '0 12px 32px rgba(0,0,0,.08)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 800, mb: 1 }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ color: '#64748b', lineHeight: 1.6 }}>
                    {f.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== CTA ===== */}
      <Divider />

      <Box sx={{ py: 10 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, mb: 2 }}>
            Bağış süreçlerini güvenle yönetin
          </Typography>

          <Button
            variant="contained"
            size="large"
            sx={{ borderRadius: 3, px: 6, py: 1.6 }}
          >
            Ücretsiz Deneyin
          </Button>
        </Container>
      </Box>

    </Box>
  );
};

export default BagisYonetimiPage;